import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { TargetedEvent } from 'preact/compat';
import { useEffect, useMemo } from 'preact/hooks';

import { OutlineButton } from '@/components/Buttons';
import Loader from '@/components/Loader';
import { useAnnouncementsQueries } from '@/hooks/announcements';
import { usePinnedNotifications, useReadAllNotifications, useReadNotificationMutation } from '@/hooks/notifications';
import { cn } from '@/lib/utils';
import { getNotificationsInfiniteQueryOptions } from '@/queries/notifications';

import Announcements from '../Announcements';
import EmptyNotifications from './EmptyNotifiications';
import Notification from './Notification';
import PinnedNotification from './PinnedNotification';

const Notifications = ({ className }: { className?: string }) => {
  const queryClient = useQueryClient();

  const {
    data: announcements,
    isLoading: isLoadingAnnouncements,
    isFetching: isFetchingAnnouncements,
  } = useAnnouncementsQueries();
  const {
    data: { list: notifications = undefined } = {},
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingNotifications,
    isFetching: isFetchingNotifications,
  } = useInfiniteQuery(getNotificationsInfiniteQueryOptions());
  const { data: pinned, isLoading: isLoadingPinned } = usePinnedNotifications();
  const { mutate: markAsRead, isPending: loading } = useReadAllNotifications();
  const { mutate: viewNotification } = useReadNotificationMutation();

  const hasNotifications =
    (pinned?.list.length ?? 0) > 0 || (announcements?.length ?? 0) > 0 || (notifications?.length ?? 0) > 0;
  // The pinned strip is counted here too: a user whose only notification is a pinned one would
  // otherwise be shown EmptyNotifications until that request lands.
  const isLoading = isLoadingAnnouncements || isLoadingNotifications || isLoadingPinned;
  const isFetching = isFetchingAnnouncements || isFetchingNotifications;

  const onScrollHandler = useMemo(
    () =>
      debounce(
        (event: TargetedEvent<HTMLDivElement>) => {
          const target = event.target as HTMLDivElement;
          if (
            target &&
            target.scrollHeight - (target.scrollTop + target.clientHeight) < 10 &&
            hasNextPage &&
            !isLoadingNotifications
          ) {
            fetchNextPage();
          }
        },
        120,
        { trailing: true },
      ),
    [fetchNextPage, hasNextPage, isLoadingNotifications],
  );

  // Reset notifications query to prevent it fetching multiple pages when re-mounting
  useEffect(
    () => () => {
      queryClient.resetQueries({ queryKey: getNotificationsInfiniteQueryOptions().queryKey });
    },
    [queryClient],
  );

  return (
    <section className={cn('flex h-full w-full flex-col bg-white', className)}>
      <div className="flex items-center justify-between p-4">
        <h4 className="preflight preflight-h4 font-semibold">All Notifications</h4>
        {hasNotifications && (
          <OutlineButton loading={loading} size="medium" label="Mark as read" onClick={() => markAsRead()} />
        )}
      </div>
      <div className="customScrollBar grey min-h-0 grow" onScroll={onScrollHandler}>
        {isLoading ? (
          <Loader fill />
        ) : !hasNotifications ? (
          <EmptyNotifications />
        ) : (
          <>
            {(pinned?.list.length ?? 0) > 0 && (
              <ul className="preflight preflight-ul mb-2 flex flex-col gap-2 px-2">
                {pinned?.list.map((item) => (
                  <li key={item._id}>
                    <PinnedNotification notification={item} onClick={viewNotification} />
                  </li>
                ))}
              </ul>
            )}

            <Announcements data={announcements} />

            <ul className="preflight preflight-ul px-2 pb-6">
              {notifications?.map((item) => (
                <li key={item._id}>
                  <Notification
                    id={item._id}
                    title={item.title}
                    description={item.body}
                    time={item.createdAt}
                    media={item.previewImage}
                    viewed={item.isViewed}
                    actionURL={item.actionURL}
                    type={item.type}
                    onClick={viewNotification}
                  />
                </li>
              ))}
              {isFetching && (
                <li>
                  <Loader width="24px" height="24px" className="mt-2" />
                </li>
              )}
            </ul>
          </>
        )}
      </div>
    </section>
  );
};

export default Notifications;
